import {
  useComments,
  useEditComment,
  useDeleteComment,
  mapCommentDtoToEnhancedComment,
  type EnhancedComment,
  CommentsCategory,
  CommentsSortBy,
  CommentsSortOrder
} from '@domain/comments'
import {ChipsFilter} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {KpiType} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Stack} from '@mui/material'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {CommentBoxComments} from '../components'
import {FEATURE_NAMESPACE, PERFORMANCE_REVIEW_KPI_TYPES} from '../consts'
import {PerformanceReviewQueryKey} from '../types'
import {parseRegionalManagerCommentsCategoryInstanceId} from '../utils'

const queryKeyPrefix = PerformanceReviewQueryKey.RegionalManagerKpisComments

type Props = {
  plantId: string
  commentCategoryInstanceIds: string[]
  selectedCommentsKpiTypes: KpiType[]
  onSelectedCommentsKpiTypesChange: (kpiTypes: KpiType[]) => void
}

const commentsCategory = CommentsCategory.HrocRegionalManagerKpis
const commentsSortOrder = CommentsSortOrder.Descending
const commentsSortBy = CommentsSortBy.CreatedOn

export const RegionalManagerKpisCommentsContainer = ({
  plantId,
  commentCategoryInstanceIds,
  selectedCommentsKpiTypes,
  onSelectedCommentsKpiTypesChange
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const timezone = useUpmPlantTimezone(plantId)
  const {loggedUser} = useUserContext()
  const currentUserName = loggedUser?.name

  const {
    data: comments = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useComments(
    {
      plantId,
      commentCategoryInstanceIds,
      commentsCategory,
      commentsSortOrder,
      commentsSortBy
    },
    queryKeyPrefix
  )
  const {mutateAsync: editComment} = useEditComment(
    {
      plantId,
      commentsCategory
    },
    queryKeyPrefix
  )
  const {mutateAsync: deleteComment} = useDeleteComment(
    {
      plantId,
      commentsCategory
    },
    queryKeyPrefix
  )

  const handleDeleteComment = async ({id, commentsCategoryInstanceId}: EnhancedComment) => {
    await deleteComment({id, commentCategoryInstanceId: commentsCategoryInstanceId})
  }

  const handleEditComment = async (
    {id, commentsCategoryInstanceId}: EnhancedComment,
    value: string
  ) => {
    await editComment({value, id, commentCategoryInstanceId: commentsCategoryInstanceId})
  }

  const kpiOptions = PERFORMANCE_REVIEW_KPI_TYPES.map((kpi) => ({
    label: t(`kpis.label.${kpi}`),
    value: kpi
  }))

  const enhancedComments: EnhancedComment[] = useMemo(() => {
    const createPillText = (commentsCategoryInstanceId: string): string => {
      const parsedInstanceId = parseRegionalManagerCommentsCategoryInstanceId(
        commentsCategoryInstanceId
      )
      const kpiType = parsedInstanceId?.kpiType
      return kpiType ? t(`kpis.label.${kpiType}`) : ''
    }

    return comments.map((comment) => {
      const mappedComment = mapCommentDtoToEnhancedComment(
        comment,
        timezone,
        language,
        currentUserName
      )
      const pillText = createPillText(comment.commentsCategoryInstanceId)

      const enhancedComment = pillText
        ? {
            ...mappedComment,
            pill: {
              type: 'neutral' as const,
              text: pillText
            }
          }
        : mappedComment

      return enhancedComment
    })
  }, [comments, timezone, language, currentUserName, t])

  return (
    <Stack spacing={1}>
      <ChipsFilter
        options={kpiOptions}
        selectedOptions={selectedCommentsKpiTypes}
        onChange={onSelectedCommentsKpiTypesChange}
        t={t}
      />
      <DataContentWrapper
        data={enhancedComments}
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        retryFunction={refetch}
        progressSx={{color: 'primary.main'}}
      >
        <CommentBoxComments
          comments={enhancedComments}
          onDelete={handleDeleteComment}
          onEdit={handleEditComment}
        />
      </DataContentWrapper>
    </Stack>
  )
}
